import { formatDate, plural, titleCase } from '@westhetics/ui'
import type { Category, Template, TemplateClinic, TemplateRef } from './api'
import { AUDIENCE_LABELS } from './labels'

/**
 * A ativação do documento por clínica, em palavras (docs/areas/documentos-e-assinaturas.md,
 * RN-DOC-05, 06 e 48 a 50). Aqui não há regra nova: a API decide; estas funções só dizem na tela o
 * que ela vai aceitar e por quê, para nenhuma ação ficar muda.
 */

/** Categorias em que a clínica usa um documento só por vez (RN-DOC-06). */
const EXCLUSIVE_NOUN: Partial<Record<Category, string>> = {
  anamnesis: 'uma anamnese',
  service_contract: 'um contrato de prestação',
  model_patient_contract: 'um contrato de paciente modelo',
}

export function isExclusiveCategory(category: Category) {
  return category in EXCLUSIVE_NOUN
}

/** Categorias em que a clínica não pode ficar sem documento ativo (RN-DOC-49). */
export function isRequiredCategory(category: Category) {
  return category === 'anamnesis' || category === 'service_contract'
}

type ActivationTemplate = Pick<Template, 'category' | 'audience' | 'isDefault' | 'procedures'>

/** O documento chega às clínicas por procedimento do catálogo, sem ativação (RN-DOC-43). */
export function arrivesByProcedure(template: Pick<Template, 'procedures'>) {
  return template.procedures.length > 0
}

/** O parágrafo que abre a aba: como este documento chega a uma clínica. */
export function activationIntro(template: ActivationTemplate): string {
  const unchanged =
    'Ativar ou desativar vale para as próximas emissões: o que já foi emitido ou assinado não muda.'
  if (arrivesByProcedure(template)) {
    return `Este documento acompanha ${plural(template.procedures.length, 'procedimento')} do catálogo e chega sozinho a toda clínica que vende ${template.procedures.length === 1 ? 'esse procedimento' : 'um deles'}: não depende de ativação. A lista mostra onde ele está em uso. Ativar à mão só é preciso na clínica que deve oferecê-lo sem vender o procedimento. ${unchanged}`
  }
  const noun = EXCLUSIVE_NOUN[template.category]
  if (noun) {
    return `Cada clínica usa ${noun} por vez: ativar este documento numa clínica tira o que ela usa hoje.${
      template.isDefault ? ' É o padrão do catálogo e entra sozinho em toda clínica nova.' : ''
    } ${unchanged}`
  }
  return `A clínica só oferece este documento quando ele está ativo nela.${
    template.isDefault
      ? ` É padrão do catálogo e entra sozinho em toda clínica nova que emite para ${AUDIENCE_LABELS[template.audience]}.`
      : ''
  } ${unchanged}`
}

/** Dá para ativar: a clínica não está bloqueada e emite para o público do documento. */
export function canActivate(row: TemplateClinic) {
  return !row.isActive && row.hasAudience && row.clinicStatus !== 'blocked'
}

/**
 * Dá para desativar: onde a clínica não pode ficar sem (anamnese, contrato), só quando há um padrão
 * do catálogo para assumir.
 */
export function canDeactivate(
  row: TemplateClinic,
  template: Pick<Template, 'category'>,
  fallback: TemplateRef | null,
) {
  return row.isActive && (!isRequiredCategory(template.category) || fallback !== null)
}

export interface ClinicSituation {
  /** O status por extenso. */
  label: string
  level: 'done' | 'waiting'
  /** Quem, quando e de onde veio; ou por que o documento não está na clínica. */
  detail: string | null
  /** Por que não há ação nesta linha, quando não há. */
  reason: string | null
}

function onDate(iso: string) {
  return formatDate(new Date(iso), { year: true })
}

/** A situação do documento numa clínica, com o registro de quem ativou ou desativou. */
export function clinicSituation(
  row: TemplateClinic,
  template: Pick<Template, 'category' | 'audience'>,
  fallback: TemplateRef | null,
): ClinicSituation {
  const procedures = row.procedures.map((procedure) => procedure.name).join(' · ')

  if (row.isActive) {
    const since = row.activatedAt ? onDate(row.activatedAt) : null
    const origin =
      row.source === 'default'
        ? `padrão do catálogo${since ? `, desde ${since}` : ''}`
        : `ativado por ${titleCase(row.activatedBy?.name ?? 'Westhetics')}${since ? ` em ${since}` : ''}`
    return {
      label: 'Ativo',
      level: 'done',
      detail: procedures ? `${origin} · também chega com ${procedures}` : origin,
      reason: canDeactivate(row, template, fallback)
        ? null
        : template.category === 'anamnesis'
          ? 'Sai sozinha quando outra anamnese for ativada nesta clínica.'
          : 'Sai sozinho quando outro contrato for ativado nesta clínica.',
    }
  }

  const reason =
    row.clinicStatus === 'blocked'
      ? 'Clínica bloqueada: desbloqueie antes de ativar.'
      : !row.hasAudience
        ? `A clínica não emite para ${AUDIENCE_LABELS[template.audience]}: inclua o público no cadastro dela antes de ativar.`
        : null

  if (procedures) {
    return {
      label: 'Em uso por procedimento',
      level: 'done',
      detail: `chega com ${procedures}`,
      reason,
    }
  }
  return {
    label: 'Não ativo',
    level: 'waiting',
    detail: row.activeInCategory
      ? `usa "${row.activeInCategory.name}"`
      : row.deactivatedAt
        ? `desativado por ${titleCase(row.deactivatedBy?.name ?? 'Westhetics')} em ${onDate(row.deactivatedAt)}`
        : null,
    reason,
  }
}
